import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import type { CreateProjectInput, Project, UpdateProjectInput } from "../../lib/api";
import { listTimezones } from "../../lib/timezones";

interface ProjectFormValues {
  code: string;
  name: string;
  location: string;
  timezone: string;
  // Held as a string, like every other numeric field in these forms, so the
  // input can legitimately be empty while someone is retyping it rather than
  // snapping back to 0 on the first keystroke.
  maxConcurrentStreams: string;
}

type FieldErrors = Partial<Record<keyof ProjectFormValues, string>>;

// Defaults a new project to whichever timezone the browser itself is in —
// a better guess than hardcoding one zone, since whoever is filling this
// form out is usually near (or working the same hours as) the site they're
// adding.
function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "";
  }
}

function emptyValues(): ProjectFormValues {
  return { code: "", name: "", location: "", timezone: browserTimezone(), maxConcurrentStreams: "" };
}

interface ProjectFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for edit, absent for create. */
  editing: Project | null;
  isSaving: boolean;
  onCreate: (input: CreateProjectInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateProjectInput) => Promise<unknown>;
}

// One dialog handles both create and edit, same reasoning as every other
// form dialog in this app (CameraFormDialog, RoleFormDialog, ...).
export function ProjectFormDialog({ isOpen, onOpenChange, editing, isSaving, onCreate, onUpdate }: ProjectFormDialogProps) {
  const [values, setValues] = useState<ProjectFormValues>(emptyValues);
  // Field-level errors mark the offending TextInput/Selector directly
  // (status="error") instead of a single string at the bottom of the form —
  // requestError is reserved for failures the fields themselves can't
  // predict (a 409 from the server after every field already validated).
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);

  const timezoneOptions = useMemo(() => listTimezones().map((tz) => ({ value: tz, label: tz })), []);

  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    setRequestError(null);
    if (editing) {
      setValues({
        code: editing.code,
        name: editing.name,
        location: editing.location,
        timezone: editing.timezone,
        // Zero means "no site limit", which reads better as an empty box than
        // as a 0 an operator might take for "no streams allowed".
        maxConcurrentStreams: editing.max_concurrent_streams ? String(editing.max_concurrent_streams) : "",
      });
    } else {
      setValues(emptyValues());
    }
  }, [isOpen, editing]);

  const set = <K extends keyof ProjectFormValues>(key: K) => (value: string) =>
    setValues((v) => ({ ...v, [key]: value }));

  const statusFor = (field: keyof ProjectFormValues) =>
    fieldErrors[field] ? ({ type: "error", message: fieldErrors[field] } as const) : undefined;

  const handleSubmit = async () => {
    setRequestError(null);

    const errors: FieldErrors = {};
    if (!values.code.trim()) errors.code = "Project code is required.";
    if (!values.name.trim()) errors.name = "Name is required.";
    if (!values.timezone.trim()) errors.timezone = "Timezone is required.";
    const maxStreams = values.maxConcurrentStreams.trim() === "" ? 0 : Number(values.maxConcurrentStreams);
    if (!Number.isInteger(maxStreams) || maxStreams < 0) {
      errors.maxConcurrentStreams = "Enter a whole number, or leave blank for no limit.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const input = {
        code: values.code.trim(),
        name: values.name.trim(),
        location: values.location.trim(),
        timezone: values.timezone,
        max_concurrent_streams: maxStreams,
      };
      if (editing) {
        await onUpdate(editing.id, { ...input, version: editing.version });
      } else {
        await onCreate(input);
      }
      onOpenChange(false);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  // No <form> element wraps this dialog's fields — see CameraFormDialog's
  // own comment on why TextInput's onEnter is used instead.
  const submitOnEnter = () => {
    if (!isSaving) void handleSubmit();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={480}>
      <Layout
        header={
          <DialogHeader
            title={editing ? `Edit ${editing.name}` : "Add project"}
            subtitle="A project (or site) is what cameras belong to, and what camera access is granted by."
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <TextInput
                label="Project code"
                description="A short, unique identifier for this site, e.g. PIT3N"
                value={values.code}
                onChange={set("code")}
                status={statusFor("code")}
                isRequired
                hasAutoFocus
                onEnter={submitOnEnter}
              />
              <TextInput
                label="Project name"
                value={values.name}
                onChange={set("name")}
                status={statusFor("name")}
                isRequired
                onEnter={submitOnEnter}
              />
              <TextInput
                label="Location"
                value={values.location}
                onChange={set("location")}
                onEnter={submitOnEnter}
              />
              <TextInput
                label="Max concurrent streams"
                description="How many of this site's cameras may be watched at once. Leave blank for no site limit. Each site reaches Head Office over its own link, so this is the number that protects it."
                value={values.maxConcurrentStreams}
                onChange={set("maxConcurrentStreams")}
                status={statusFor("maxConcurrentStreams")}
                onEnter={submitOnEnter}
              />
              <Selector
                label="Timezone"
                value={values.timezone}
                onChange={set("timezone")}
                options={timezoneOptions}
                placeholder="Choose a timezone"
                status={statusFor("timezone")}
                hasSearch
                isRequired
              />
              {requestError && <Banner status="error" title="Couldn't save project" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button label="Cancel" variant="ghost" onClick={() => onOpenChange(false)} isDisabled={isSaving} />
              <Button
                label={editing ? "Save changes" : "Add project"}
                variant="primary"
                clickAction={handleSubmit}
                isLoading={isSaving}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
